import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, logout, setAuthInProgress } from '../redux/slices/authSlice';
import { login, autoLogin, autoLoginBrowser, logout as logoutAPI, telegramAuth } from '../api/auth';
import { httpClient, setAuthModalFunction } from '../api/httpClient';
import { getMe } from '../api/auth';
import { fetchUserLevel, clearUserLevel } from '../redux/slices/userLevelSlice';
import { fetchGameDataSummary, fetchEnergyStatus } from '../redux/slices/gameDataSlice';
import { fetchUnreadCount } from '../redux/slices/notificationsSlice';
import { getCollectionCategoriesGrouped } from '../api/collections';
import { hasSavedCredentials, deleteCookie } from '../utils/cookies';
import { isTelegramWebApp, getTelegramUser } from '../utils/telegram';
import { useQueryClient } from 'react-query';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.auth.user);
    const authInProgress = useSelector(state => state.auth.authInProgress);
    const [isLoading, setIsLoading] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [telegramAuthAttempted, setTelegramAuthAttempted] = useState(false);
    const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
    const queryClient = useQueryClient();

    // Функция для prefetch популярных данных
    const prefetchPopularData = async () => {
        try {
            console.log('🚀 Prefetching popular data...');
            // Prefetch категории галереи (ID=9)
            await queryClient.prefetchQuery(
                ['collectionCategoriesGrouped', 9],
                () => getCollectionCategoriesGrouped(9),
                {
                    staleTime: 5 * 60 * 1000, // 5 минут
                    cacheTime: 10 * 60 * 1000, // 10 минут
                }
            );
            console.log('✅ Popular data prefetched successfully');
        } catch (error) {
            console.error('❌ Error prefetching popular data:', error);
        }
    };

    // Передаем функцию показа модального окна в httpClient
    useEffect(() => {
        setAuthModalFunction(setShowLoginModal);
    }, []);

    // Обработка ошибок аутентификации от httpClient
    useEffect(() => {
        const handleAuthError = (error) => {
            console.log('🔐 Auth error received:', error);

            // Проверяем НАСТОЯЩИЙ Telegram WebApp с данными пользователя
            const hasValidTelegramData = !!(
                window.Telegram?.WebApp?.initData &&
                window.Telegram?.WebApp?.initData.length > 0 &&
                window.Telegram?.WebApp?.initDataUnsafe?.user?.id
            );

            // Также проверяем наличие Telegram WebApp объекта вообще
            const isTelegramEnvironment = !!window.Telegram?.WebApp;

            console.log('🔐 handleAuthError: проверяем Telegram WebApp:', {
                hasWebApp: !!window.Telegram?.WebApp,
                hasValidTelegramData,
                isTelegramEnvironment,
                initData: window.Telegram?.WebApp?.initData || 'undefined',
                userId: window.Telegram?.WebApp?.initDataUnsafe?.user?.id || 'undefined'
            });

            // В Telegram среде НИКОГДА не делаем редирект на страницу авторизации
            if (isTelegramEnvironment) {
                console.log('🔐 НЕ делаем редирект - это Telegram среда, ждём авторизации через initData');
                return;
            }

            // Делаем редирект на страницу авторизации только в обычном браузере
            if (error.code === 'AUTH_REQUIRED') {
                console.log('🔐 Делаем редирект на страницу авторизации - обычный браузер');

                // Определяем базовый путь для редиректа
                const currentPath = window.location.pathname;

                // Проверяем, не находимся ли мы уже на странице авторизации
                if (currentPath === '/auth' || currentPath === '/telegram/auth') {
                    console.log('🔐 Уже на странице авторизации - редирект не нужен');
                    return;
                }

                const authUrl = `/auth?returnTo=${encodeURIComponent(currentPath)}`;
                console.log('🔐 Редирект на:', authUrl);
                window.location.href = authUrl;
            }
        };

        httpClient.onAuthError(handleAuthError);
        return () => {
            httpClient.offAuthError(handleAuthError);
        };
    }, []);

    // Автоматическая инициализация при загрузке приложения
    useEffect(() => {
        const initializeAuth = async () => {
            console.log('🔐 AuthContext: Начинаем инициализацию авторизации...');
            
            // Устанавливаем флаг авторизации в процессе
            dispatch(setAuthInProgress(true));
            
            try {
                // Используем утилиту для определения Telegram среды
                const isTelegramApp = isTelegramWebApp();
            
            if (isTelegramApp) {
                // В Telegram WebApp авторизуемся через initData ТОЛЬКО ОДИН РАЗ
                const telegramUser = getTelegramUser();
                if (telegramUser && !user && !telegramAuthAttempted) {
                    console.log('📱 Telegram WebApp: авторизуемся через данные пользователя:', telegramUser);
                    setTelegramAuthAttempted(true);
                    
                    try {
                        const authResponse = await telegramAuth({
                            telegram_id: telegramUser.id,
                            telegram_username: telegramUser.username,
                            first_name: telegramUser.first_name,
                            last_name: telegramUser.last_name
                        });
                        console.log('✅ Telegram WebApp авторизация успешна:', authResponse);
                        
                        if (authResponse?.user) {
                            dispatch(setUser(authResponse.user));
                            
                            // Загружаем критические данные последовательно
                            console.log('🔄 AuthContext: Загружаем критические данные...');
                            await Promise.all([
                                dispatch(fetchUserLevel()),
                                dispatch(fetchGameDataSummary()),
                                dispatch(fetchEnergyStatus()),
                                dispatch(fetchUnreadCount())
                            ]);
                            
                            // Prefetch в фоне (не блокирует UI)
                            prefetchPopularData();
                            console.log('🔐 AuthContext: Пользователь установлен в Redux:', authResponse.user);
                        } else {
                            // Фолбэк: запрашиваем текущего пользователя через /auth/me только если нет пользователя
                            if (!user && !authInProgress) {
                                try {
                                    const me = await getMe();
                                    if (me) {
                                        dispatch(setUser(me));
                                        
                                        // Загружаем критические данные последовательно
                                        console.log('🔄 AuthContext: Загружаем критические данные...');
                                        await Promise.all([
                                            dispatch(fetchUserLevel()),
                                            dispatch(fetchGameDataSummary()),
                                            dispatch(fetchEnergyStatus()),
                                            dispatch(fetchUnreadCount())
                                        ]);
                                        
                                        // Prefetch в фоне (не блокирует UI)
                                        prefetchPopularData();
                                        console.log('🔐 AuthContext: Пользователь получен через /auth/me:', me);
                                    }
                                } catch (meError) {
                                    console.error('AuthContext - ошибка получения /auth/me после telegramAuth:', meError);
                                }
                            } else {
                                console.log('🔐 AuthContext: Пользователь уже есть, пропускаем /auth/me');
                            }
                        }
                    } catch (error) {
                        console.error('AuthContext - ошибка авторизации Telegram WebApp:', error);
                    }
                } else if (!telegramUser) {
                    console.log('⚠️ Telegram WebApp: данные пользователя не найдены');
                } else if (telegramAuthAttempted) {
                    console.log('📱 Telegram WebApp: авторизация уже была попытка, пользователь авторизуется через initData');
                } else {
                    console.log('📱 Telegram WebApp: пользователь уже авторизован');
                }
            } else {
                // В браузере проверяем наличие сохраненных данных
                if (hasSavedCredentials() && !autoLoginAttempted) {
                    console.log('🌐 Browser: найдены сохраненные данные, пытаемся автоматически войти');
                    setAutoLoginAttempted(true); // Устанавливаем флаг ДО вызова API
                    try {
                        const authResponse = await autoLoginBrowser();
                        console.log('🌐 Browser: автоматический вход успешен:', authResponse);
                        
                        // Сохраняем токен в localStorage
                        if (authResponse.access_token) {
                            localStorage.setItem('access_token', authResponse.access_token);
                            httpClient.setToken(authResponse.access_token);
                            
                            // Получаем данные пользователя
                            if (authInProgress) {
                                console.log('⚠️ Auth in progress, skipping getMe()');
                                return;
                            }
                            const userData = await getMe();
                            if (userData) {
                                dispatch(setUser(userData));
                                
                                // Загружаем критические данные последовательно
                                console.log('🔄 AuthContext: Загружаем критические данные...');
                                await Promise.all([
                                    dispatch(fetchUserLevel()),
                                    dispatch(fetchGameDataSummary()),
                                    dispatch(fetchEnergyStatus()),
                                    dispatch(fetchUnreadCount())
                                ]);
                                
                                // Prefetch в фоне (не блокирует UI)
                                prefetchPopularData();
                                console.log('🔐 AuthContext: Пользователь установлен в Redux (browser):', userData);
                            }
                        }
                    } catch (error) {
                        console.log('🌐 Browser: автоматический вход не удался, проверяем access_token');
                        
                        // Fallback: пытаемся через access_token только если не было попытки auto-login
                        const accessToken = localStorage.getItem('access_token');
                        if (accessToken && !user && !autoLoginAttempted) {
                            try {
                                const userData = await autoLogin();
                                console.log('🌐 Browser: авторизация через access_token успешна:', userData);
                                dispatch(setUser(userData));
                                
                                // Загружаем критические данные последовательно
                                console.log('🔄 AuthContext: Загружаем критические данные...');
                                await Promise.all([
                                    dispatch(fetchUserLevel()),
                                    dispatch(fetchGameDataSummary()),
                                    dispatch(fetchEnergyStatus()),
                                    dispatch(fetchUnreadCount())
                                ]);
                                
                                // Prefetch в фоне (не блокирует UI)
                                prefetchPopularData();
                                console.log('🔐 AuthContext: Пользователь установлен в Redux (browser):', userData);
                        } catch (tokenError) {
                            console.error('AuthContext - ошибка автоматического входа через токен:', tokenError);
                            localStorage.removeItem('access_token');
                            httpClient.resetRefreshState();
                            setAutoLoginAttempted(true); // Помечаем что попытка была
                        }
                        }
                    }
                } else {
                    console.log('🌐 Browser: нет сохраненных данных, проверяем access_token');
                    
                    // Проверяем access_token как fallback только если не было попытки auto-login
                    const accessToken = localStorage.getItem('access_token');
                    if (accessToken && !user && !autoLoginAttempted) {
                        try {
                            const userData = await autoLogin();
                            console.log('🌐 Browser: авторизация через access_token успешна:', userData);
                            dispatch(setUser(userData));
                            
                            // Загружаем критические данные последовательно
                            console.log('🔄 AuthContext: Загружаем критические данные...');
                            await Promise.all([
                                dispatch(fetchUserLevel()),
                                dispatch(fetchGameDataSummary()),
                                dispatch(fetchEnergyStatus()),
                                dispatch(fetchUnreadCount())
                            ]);
                            
                            // Prefetch в фоне (не блокирует UI)
                            prefetchPopularData();
                            console.log('🔐 AuthContext: Пользователь установлен в Redux (browser):', userData);
                        } catch (tokenError) {
                            console.error('AuthContext - ошибка автоматического входа через токен:', tokenError);
                            localStorage.removeItem('access_token');
                            httpClient.resetRefreshState();
                            setAutoLoginAttempted(true); // Помечаем что попытка была
                        }
                    } else {
                        console.log('🌐 Browser: нет данных для автоматической авторизации');
                    }
                }
            }
        } finally {
            // Снимаем флаг авторизации в процессе
            dispatch(setAuthInProgress(false));
        }
        };

        // Запускаем инициализацию только если нет пользователя
        if (!user) {
            initializeAuth();
        } else {
            console.log('🔐 AuthContext: Пользователь уже авторизован, пропускаем инициализацию');
        }
    }, [dispatch, user, telegramAuthAttempted]);

    const handleLogin = async (credentials) => {
        try {
            const response = await login(credentials);
            if (response.access_token) {
                localStorage.setItem('access_token', response.access_token);
                httpClient.setToken(response.access_token);
                
                // Получаем данные пользователя
                if (authInProgress) {
                    console.log('⚠️ Auth in progress, skipping getMe()');
                    return;
                }
                const userData = await getMe();
                if (userData) {
                    dispatch(setUser(userData));
                    
                    // Загружаем критические данные последовательно
                    console.log('🔄 AuthContext: Загружаем критические данные после логина...');
                    await Promise.all([
                        dispatch(fetchUserLevel()),
                        dispatch(fetchGameDataSummary()),
                        dispatch(fetchEnergyStatus()),
                        dispatch(fetchUnreadCount())
                    ]);
                    
                    // Prefetch в фоне (не блокирует UI)
                    prefetchPopularData();
                }
            }
            setShowLoginModal(false);
            return response;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const handleTelegramAuth = async (telegramData) => {
        try {
            const response = await telegramAuth(telegramData);

            // В Telegram WebApp НЕ сохраняем access_token и НЕ вызываем autoLogin
            // Пользователь авторизуется через initData в каждом запросе
            if (response?.user) {
                dispatch(setUser(response.user));
                
                // Загружаем критические данные последовательно
                console.log('🔄 AuthContext: Загружаем критические данные после Telegram auth...');
                await Promise.all([
                    dispatch(fetchUserLevel()),
                    dispatch(fetchGameDataSummary()),
                    dispatch(fetchEnergyStatus()),
                    dispatch(fetchUnreadCount())
                ]);
                
                // Prefetch в фоне (не блокирует UI)
                prefetchPopularData();
            } else {
                // Фолбэк: пробуем получить пользователя через /auth/me только если нет пользователя
                if (!user && !authInProgress) {
                    try {
                        const me = await getMe();
                        if (me) {
                            dispatch(setUser(me));
                            
                            // Загружаем критические данные последовательно
                            console.log('🔄 AuthContext: Загружаем критические данные после Telegram fallback...');
                            await Promise.all([
                                dispatch(fetchUserLevel()),
                                dispatch(fetchGameDataSummary()),
                                dispatch(fetchEnergyStatus()),
                                dispatch(fetchUnreadCount())
                            ]);
                            
                            // Prefetch в фоне (не блокирует UI)
                            prefetchPopularData();
                        }
                    } catch (meError) {
                        console.error('Telegram auth fallback /auth/me error:', meError);
                    }
                } else {
                    console.log('🔐 AuthContext: Пользователь уже есть, пропускаем /auth/me fallback');
                }
            }
            setShowLoginModal(false);
            return response;
        } catch (error) {
            console.error('Telegram auth error:', error);
            throw error;
        }
    };

    const handleLogout = async () => {
        try {
            await logoutAPI();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('access_token');
            httpClient.resetRefreshState();
            dispatch(logout());
            dispatch(clearUserLevel()); // Очищаем данные уровня
            setTelegramAuthAttempted(false); // Сбрасываем флаг при выходе
            
            // Очищаем cookies на фронтенде
            deleteCookie('saved_login');
            deleteCookie('saved_password');
        }
    };

    const closeLoginModal = () => {
        setShowLoginModal(false);
        // Сбрасываем флаг в httpClient чтобы можно было показать модальное окно снова
        if (httpClient.resetAuthModalFlag) {
            httpClient.resetAuthModalFlag();
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            login: handleLogin, 
            telegramAuth: handleTelegramAuth,
            logout: handleLogout,
            showLoginModal,
            // Экспортируем реальный сеттер, чтобы можно было открывать/закрывать модалку
            setShowLoginModal,
            // Дополнительно предоставляем явный метод закрытия
            closeLoginModal
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 